// Live-performance metric helper.
//
// Replaces the hard-coded `metrics.targetApr` headline on vault cards with a
// dynamically-derived live APR once the vault has enough share-price history.
//
// Policy (locked with JP):
//   - `kind: 'target'` displayed when live data < 14 days (or unavailable).
//     "Target APR ~XX%" / "backtest est."
//   - `kind: 'live'` displayed once ≥ 14 days of share-price snapshots exist.
//     "Live APR ~XX%" / "Nd live"  (N = the vault's TRUE contract age, from
//     vaultCfg.launchDate — NOT the APR window. See below.)
//   - The stealth flag (APP_MODE.stealth) gates the *deposit CTA* only — it
//     does NOT gate live APR. Share-price data is on-chain public anyway;
//     showing the honest live number is better than parading a stale
//     backtest target. Stealth + ≥ 14 d data still shows live APR.
//   - Annualization window = the FULL available price-history span (no cap).
//     A previous 30-day cap silently chopped off early performance for any
//     vault older than a month, AND the "Nd live" label inherited that cap so
//     every >30 d vault read "30d live" regardless of its real age. The
//     14-day floor (MIN_DAYS_FOR_LIVE) still gates display: 7 d would feed the
//     pakhom +1180% trap.
//   - "Nd live" is the vault's CONTRACT AGE (now − launchDate), which can
//     exceed the price-history span when share-price logging started after
//     deploy (e.g. Onyx: live Apr 13, logging from Apr 20). We show the honest
//     age and annualize over whatever price data exists.
//   - APR floor = -99% (we never display "-100%" / "-∞" even on a vault
//     that's been liquidated). No upper cap — high-growth numbers are real.
//
// Math: SIMPLE annualized return — return × 365/days — measured against the
// vault's LAUNCH share price (the dashboard/factsheet normalization base), NOT
// the first available snapshot. Both partner-facing surfaces (these cards + the
// PDF factsheet) MUST use the same formula or they disagree on screen; the
// factsheet uses simple, so we use simple. A four-digit compound APR also reads
// as degen/ponzi — the 14-day floor + the "Nd live" label + factsheet footnote
// carry the honesty instead. (JP's call, 2026-06-29 — was compound before.)
//   return = (raw_pps / launchSharePrice) - 1   where raw_pps = (ta/ts_)*1000
//   APR    = return × 365 / days_live
//
// Data source: /api/vault/<key>/share-price-history — the runner's
// share-price snapshots, served via crystalclear-api (Lambda or local
// dev). Returns {points: [{ts, ta, ts_}]} sorted ascending.
//
// Pre-2026-05-11 evening this fetched the raw JSONL directly from
// /data/<vault>_share_price_<ym>.jsonl. After the serverless cutover
// those files only live in the crystalclear-vault-data S3 bucket
// (not the frontend bucket), so CloudFront returned 403 and every
// vault silently fell back to "Target APR — backtest est." even when
// real live data existed. Routing through the API endpoint fixes
// that AND avoids adding a second CloudFront origin.
//
// Each record: {ts: ms_epoch, ta: total_assets_raw_str, ts_: total_supply_raw_str}.
// Share price ratio = ta / ts_ — proportional to USDC-per-share, sufficient
// for APR math (ratio cancels the unit factor).
//
// Cache: 5-min TTL in localStorage so repeat visits don't re-fetch on every
// card render. In-memory inflight de-dup so concurrent calls share a fetch.

import { VAULT_CONFIGS } from './config.js';

// API base — same pattern as hlFeed.js. Empty string means "same origin"
// (production: app.crystalclear.finance). Override via window.CC_API_BASE
// for dev/staging.
const API_BASE = (typeof window !== 'undefined' && typeof window.CC_API_BASE === 'string')
  ? window.CC_API_BASE
  : '';

const CACHE_KEY = "cv_perf_v2";  // v2: simple-annualized APR + live maxDD (was v1 compound)
const CACHE_TTL_MS = 5 * 60 * 1000;
// Honesty gate: don't display annualized APR until 14 days of data exist.
// 7-day windows inflate hot weeks into dishonest +1180% numbers (pakhom).
// 14 d is the balance: still produces stable enough math, doesn't make us
// wait a full month before any live data shows.
const MIN_DAYS_FOR_LIVE = 14;
const APR_FLOOR_PCT = -99;

// ── IO ────────────────────────────────────────────────────────────────

async function _fetchVaultHistory(vaultKey) {
  // Live APR only needs the first + last snapshot, so use the endpoint's
  // summary mode — returns 2 points (~200 B) instead of every 5-min snapshot
  // (~480 KB). That payload was the 3-4s "Target APR → Live APR" flicker.
  // days=180 (the endpoint max) so we capture full inception as vaults age;
  // with summary it's still just 2 points.
  const url = `${API_BASE}/api/vault/${vaultKey.toLowerCase()}/share-price-history?days=180&summary=1`;
  let payload;
  try {
    const r = await fetch(url, { cache: "default" });
    if (!r.ok) return null;
    payload = await r.json();
  } catch {
    return null;
  }
  const points = payload?.points || [];
  if (!points.length) return null;
  const out = [];
  for (const row of points) {
    const ta = Number(row.ta);
    const supply = Number(row.ts_);
    const ts = Number(row.ts);
    if (!isFinite(ta) || !isFinite(supply) || supply <= 0 || !isFinite(ts)) continue;
    // ratio is proportional to USDC-per-share — unit factor cancels in APR math
    out.push({ ts, ratio: ta / supply });
  }
  if (!out.length) return null;
  // Backend already sorts ascending, but dedup as a defense against
  // any double-write at the file/sidecar boundary.
  const dedup = new Map();
  for (const p of out) dedup.set(p.ts, p);
  const history = Array.from(dedup.values()).sort((a, b) => a.ts - b.ts);
  // Server computes max drawdown over the FULL series (summary mode) and returns
  // it as a scalar so the cards never pull the ~800 KB series. null when absent.
  const maxDdPct = (typeof payload.max_drawdown_pct === "number") ? payload.max_drawdown_pct : null;
  return { history, maxDdPct };
}

function _computeApr(history, vaultCfg) {
  if (!history || history.length < 2) return null;
  const now = history[history.length - 1];
  const first = history[0];
  // windowDays = price-history span — used ONLY as the data-sufficiency gate.
  const windowDays = (now.ts - first.ts) / 86400000;
  if (windowDays < MIN_DAYS_FOR_LIVE) return null;
  // Return vs the LAUNCH price (normalization base = launchSharePrice), matching
  // the factsheet exactly. raw pps = (ta/ts_)*1000; normalized = raw/launchPps.
  const launchPps = (vaultCfg && vaultCfg.launchSharePrice) || 1.0;
  const ret = (now.ratio * 1000) / launchPps - 1;
  if (!isFinite(ret)) return null;
  // SIMPLE annualization over the vault's CONTRACT AGE (days since launchDate),
  // so this card agrees with the factsheet. See header comment.
  const daysLive = _daysLive(vaultCfg) || Math.round(windowDays);
  const aprPct = (ret * 365 / Math.max(1, daysLive)) * 100;
  return {
    aprPct: Math.max(aprPct, APR_FLOOR_PCT),
    windowDays: Math.round(windowDays),
  };
}

// Current normalized share price from the LAST snapshot of the same series that
// feeds APR/maxDD. Independent of the ≥14d live-APR gate so even a young vault
// shows a real price. raw pps = (ta/ts_)*1000; displayed = raw / launchSharePrice
// (matches pages/vaults.js's on-chain math). Returns null if no data.
function _sharePriceFromHistory(history, vaultCfg) {
  if (!history || !history.length) return null;
  const last = history[history.length - 1];
  const launchPps = (vaultCfg && vaultCfg.launchSharePrice) || 1.0;
  const sp = (last.ratio * 1000) / launchPps;
  return isFinite(sp) && sp > 0 ? sp : null;
}

// ── Cache ─────────────────────────────────────────────────────────────

function _readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed?._ts || Date.now() - parsed._ts > CACHE_TTL_MS) return {};
    return parsed.data || {};
  } catch {
    return {};
  }
}

function _writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ _ts: Date.now(), data }));
  } catch { /* localStorage quota — silently skip */ }
}

const _inflight = new Map();

// ── Public API ────────────────────────────────────────────────────────

/**
 * Returns the performance metric to display for one vault.
 *   { kind: 'target' | 'live', label, value, subtitle, aprPct?, days? }
 * Always resolves (worst case → target fallback). Never throws.
 */
export async function getLivePerformance(vaultKey, vaultCfg) {
  // No stealth gate — see header comment. We show live APR as soon as we have
  // ≥ 14 d of data, regardless of whether deposits are open yet.

  const cached = _readCache()[vaultKey];
  if (cached) return cached;

  if (_inflight.has(vaultKey)) return _inflight.get(vaultKey);

  const promise = (async () => {
    let result;
    try {
      const fetched = await _fetchVaultHistory(vaultKey);
      const history = fetched ? fetched.history : null;
      const live = _computeApr(history, vaultCfg);
      result = live ? _liveResult(live, vaultCfg) : _targetResult(vaultCfg);
      result.maxDdPct = fetched ? fetched.maxDdPct : null;  // live max-drawdown (or null → keep config)
      result.sharePrice = _sharePriceFromHistory(history, vaultCfg);  // current normalized pps (reliable Lambda source)
    } catch {
      result = _targetResult(vaultCfg);
      result.maxDdPct = null;
      result.sharePrice = null;
    }
    const cache = _readCache();
    cache[vaultKey] = result;
    _writeCache(cache);
    return result;
  })();
  _inflight.set(vaultKey, promise);
  try { return await promise; }
  finally { _inflight.delete(vaultKey); }
}

// True contract age in whole days from vaultCfg.launchDate (the on-chain
// "live since" date). Returns null if unset/invalid so the caller can fall
// back to the price-history span.
function _daysLive(vaultCfg) {
  const d = vaultCfg?.launchDate;
  if (!d) return null;
  const ms = Date.now() - Date.parse(d);
  if (!isFinite(ms) || ms < 0) return null;
  return Math.floor(ms / 86400000);
}

function _liveResult({ aprPct, windowDays }, vaultCfg) {
  const sign = aprPct >= 0 ? "+" : "";
  // "Nd live" = true contract age (launchDate), NOT the APR window. For a vault
  // whose price logging began after deploy, the contract is older than the
  // price-history span; we show the honest age and annualize over the data we
  // have. Fall back to the data span when no launchDate is configured.
  const daysLive = _daysLive(vaultCfg) ?? windowDays;
  return {
    kind: "live",
    aprPct,
    days: daysLive,
    label: "Live APR",
    value: `${sign}${aprPct.toFixed(1)}%`,
    subtitle: `${daysLive}d live`,
  };
}

function _targetResult(vaultCfg) {
  const target = vaultCfg?.metrics?.targetApr;
  return {
    kind: "target",
    aprPct: target,
    days: null,
    label: "Target APR",
    value: target != null ? `~${target}%` : "—",
    subtitle: "backtest est.",
  };
}

/**
 * Find all `[data-apr-cell="<vaultKey>"]` elements on the page and update
 * their inner `[data-apr-label]`, `[data-apr-value]`, `[data-apr-subtitle]`
 * text nodes with the live (or fallback target) numbers. Safe to call after
 * any re-render; idempotent and bounded by the localStorage cache.
 */
export async function refreshAllAprCells() {
  const cells = document.querySelectorAll("[data-apr-cell]");
  await Promise.all(Array.from(cells).map(async (cell) => {
    const vaultKey = cell.dataset.aprCell;
    if (!vaultKey) return;
    const vaultCfg = VAULT_CONFIGS.find((v) => v.key === vaultKey);
    if (!vaultCfg) return;
    // Skip coming-soon vaults — no JSONL data, and the placeholder dash is
    // already what we want to display.
    if (vaultCfg.status !== "live") return;
    const perf = await getLivePerformance(vaultKey, vaultCfg);
    _applyToCell(cell, perf);
  }));
}

function _applyToCell(cell, perf) {
  const labelEl = cell.querySelector("[data-apr-label]");
  const valueEl = cell.querySelector("[data-apr-value]");
  const subEl   = cell.querySelector("[data-apr-subtitle]");
  if (labelEl) labelEl.textContent = perf.label;
  if (valueEl) valueEl.textContent = perf.value;
  if (subEl)   subEl.textContent   = perf.subtitle;
}

/**
 * Upgrade the Max DD tile on each live vault card from the static config.js
 * backtest value to the LIVE max drawdown — computed server-side over the full
 * share-price series and returned as a scalar by share-price-history?summary=1,
 * so the card never pulls the ~800 KB series. Shares getLivePerformance's cache
 * with the APR refresh (no extra fetch). Falls back to the config value if the
 * live number is unavailable, so the shimmer always resolves.
 * Cards: `[data-vault-key]` wrapper containing `[data-metric="max-dd"]`.
 */
export async function refreshAllMaxDdCells() {
  const cards = document.querySelectorAll("[data-vault-key]");
  await Promise.all(Array.from(cards).map(async (card) => {
    const vaultKey = card.dataset.vaultKey;
    const ddEl = card.querySelector('[data-metric="max-dd"]');
    if (!vaultKey || !ddEl) return;
    const vaultCfg = VAULT_CONFIGS.find((v) => v.key === vaultKey);
    if (!vaultCfg || vaultCfg.status !== "live") return;
    const perf = await getLivePerformance(vaultKey, vaultCfg);
    let dd = (perf && typeof perf.maxDdPct === "number") ? perf.maxDdPct : null;
    if (dd == null && vaultCfg.metrics && typeof vaultCfg.metrics.maxDD === "number") {
      dd = vaultCfg.metrics.maxDD;   // fall back to backtest so the shimmer resolves
    }
    if (dd != null) ddEl.textContent = `${dd.toFixed(1)}%`;
  }));
}

/**
 * Upgrade the Share Price tile on each live vault card from the client-side
 * on-chain read to the LIVE price served by the Lambda. The on-chain read
 * (pages/vaults.js / landing-vaults.js) fires ~4 RPC calls per card against the
 * public HL RPC; a user whose browser bursts past the 100-req/min/IP limit gets
 * every call rejected and the card silently falls back to the $1.00 default.
 * This pulls the true price from the SAME summary payload that powers APR/maxDD
 * (no extra fetch, shared cache), so the reliable edge API is the source of
 * truth and $1.00 only shows for a vault that is genuinely at $1.00.
 * Cards: `[data-vault-key]` wrapper containing `[data-metric="share-price"]`.
 */
export async function refreshAllSharePriceCells(decimals = 2) {
  const cards = document.querySelectorAll("[data-vault-key]");
  await Promise.all(Array.from(cards).map(async (card) => {
    const vaultKey = card.dataset.vaultKey;
    const spEl = card.querySelector('[data-metric="share-price"]');
    if (!vaultKey || !spEl) return;
    const vaultCfg = VAULT_CONFIGS.find((v) => v.key === vaultKey);
    if (!vaultCfg || vaultCfg.status !== "live") return;
    const perf = await getLivePerformance(vaultKey, vaultCfg);
    if (perf && typeof perf.sharePrice === "number") {
      spEl.textContent = `$${perf.sharePrice.toFixed(decimals)}`;
    }
  }));
}


// ── Live Win Rate ────────────────────────────────────────────────────────
//
// Win Rate on the Vaults page was rendered from a HARDCODED backtest target in
// config.js (e.g. Ruby = 17, by design for a high-conviction concentrated
// vault). The tooltip even claimed "(live since launch)" — a lie. Now we
// upgrade to the LIVE win rate from on-chain trades once a vault has enough
// closed positions, falling back to the backtest target below the threshold.
//
// Source: /api/account/{addr}/fills — the same endpoint just rewritten with a
// position-accumulator algorithm (split exits + same-coin concurrency now
// correctly aggregated). Returns wins / losses / win_rate over LOGICAL
// positions, where win = whole position's net PnL > 0.
//
// Threshold: ≥10 closed positions. Below that, the live number is too noisy
// to be a meaningful headline; we keep the design-intent target. Cached in
// localStorage for 5 min so cards don't refetch on every page-mount.

const _WR_CACHE_KEY = "cv_winrate_v1";
const _WR_CACHE_TTL_MS = 5 * 60 * 1000;
const _WR_MIN_TRADES = 10;

function _wrReadCache() {
  try {
    const raw = localStorage.getItem(_WR_CACHE_KEY);
    if (!raw) return {};
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > _WR_CACHE_TTL_MS) return {};
    return data || {};
  } catch { return {}; }
}
function _wrWriteCache(data) {
  try { localStorage.setItem(_WR_CACHE_KEY, JSON.stringify({ ts: Date.now(), data })); } catch {}
}

async function _fetchVaultWinRate(addr) {
  if (!addr) return null;
  const cache = _wrReadCache();
  if (cache[addr.toLowerCase()] !== undefined) return cache[addr.toLowerCase()];
  try {
    const r = await fetch(`${API_BASE}/api/account/${addr}/fills`, { cache: "default" });
    if (!r.ok) return null;
    const j = await r.json();
    const closed = j.closed_trades || 0;
    if (closed < _WR_MIN_TRADES) {
      cache[addr.toLowerCase()] = null;       // remember "not enough"
      _wrWriteCache(cache);
      return null;
    }
    const wr = Math.round(j.win_rate);
    cache[addr.toLowerCase()] = wr;
    _wrWriteCache(cache);
    return wr;
  } catch {
    return null;
  }
}

/**
 * Upgrade all `[data-winrate-value][data-vault-addr]` cells from the backtest
 * target (the initial render) to the live on-chain win rate when the vault has
 * ≥10 closed positions. Cells without an address (coming-soon / unconfigured)
 * are skipped silently.
 */
export async function refreshAllWinRateCells() {
  const cells = document.querySelectorAll("[data-winrate-value][data-vault-addr]");
  await Promise.all(Array.from(cells).map(async (el) => {
    const addr = el.dataset.vaultAddr;
    if (!addr) return;
    const wr = await _fetchVaultWinRate(addr);
    if (wr == null) return;                  // keep backtest target
    el.textContent = `${wr}%`;
  }));
}
